import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useSeo } from "@/lib/useSeo";
import { HtmlContent } from "@/components/HtmlContent";
import EntityCard from "@/components/EntityCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CalendarDays, Route, ChevronRight, Home as HomeIcon } from "lucide-react";

export default function GuideDetail() {
  const { slug } = useParams();
  const { t, pick } = useLang();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/guides/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const g = data?.guide;
  const jsonLd = useMemo(() => g ? [
    { "@context": "https://schema.org", "@type": "Article", headline: g.title, image: g.image, description: g.summary, author: { "@type": "Organization", name: "IndiaDham" } },
    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: (g.faqs || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  ] : null, [g]);
  useSeo({ title: g ? (g.seo_title || pick(g, "title")) : undefined, description: g ? (g.seo_desc || pick(g, "summary")) : undefined, image: g?.image, jsonLd });

  if (notFound) return <div className="max-w-3xl mx-auto p-12 text-center font-display text-2xl text-indigo-deep">Not found</div>;
  if (!g) return <div className="max-w-5xl mx-auto p-4"><Skeleton className="h-72 rounded-xl mb-6" /><Skeleton className="h-8 w-1/2 mb-3" /><Skeleton className="h-40" /></div>;

  return (
    <article>
      <div className="relative h-64 md:h-96 overflow-hidden bg-indigo-deep">
        <img src={resolveImg(g.image)} alt={pick(g, "title")} className="w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-indigo-deep via-indigo-deep/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 max-w-5xl mx-auto px-4 pb-6 text-white">
          <div className="flex items-center gap-1 text-xs text-white/70 mb-2">
            <Link to="/" className="hover:text-gold"><HomeIcon className="w-3 h-3 inline" /></Link><ChevronRight className="w-3 h-3" />
            <Link to="/guides" className="hover:text-gold">{t("nav_guides")}</Link><ChevronRight className="w-3 h-3" /><span>{g.dham}</span>
          </div>
          <h1 data-testid="guide-title" className="font-display text-3xl md:text-5xl font-bold leading-tight">{pick(g, "title")}</h1>
          <p className="mt-2 text-sm md:text-base text-white/80 max-w-3xl">{pick(g, "summary")}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 grid lg:grid-cols-[1fr_300px] gap-8">
        <div>
          <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 flex gap-3 items-start mb-6" data-testid="guide-best-time">
            <CalendarDays className="w-5 h-5 text-gold-dark shrink-0 mt-0.5" />
            <div><div className="text-xs uppercase tracking-wider text-gold-dark font-semibold">{t("best_time_to_visit")}</div><div className="text-sm text-indigo-deep mt-0.5">{pick(g, "best_time")}</div></div>
          </div>
          <HtmlContent html={pick(g, "content")} />
          <h2 className="font-display text-2xl text-indigo-deep mt-8 mb-3 flex items-center gap-2"><Route className="w-5 h-5 text-gold-dark" />{t("route_tips")}</h2>
          <HtmlContent html={pick(g, "route_tips")} />
          {g.faqs?.length > 0 && (
            <section className="mt-10" data-testid="guide-faqs">
              <h2 className="font-display text-2xl text-indigo-deep mb-3">{t("faqs")}</h2>
              <Accordion type="single" collapsible className="bg-white rounded-xl border border-gold/15 px-4">
                {g.faqs.map((f, i) => (
                  <AccordionItem key={f.q} value={`f${i}`}>
                    <AccordionTrigger data-testid={`faq-${i}`} className="text-left text-indigo-deep font-medium">{pick(f, "q")}</AccordionTrigger>
                    <AccordionContent className="text-indigo-deep/75">{pick(f, "a")}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}
        </div>
        <aside>
          {data.related?.length > 0 && (
            <div className="lg:sticky lg:top-20">
              <h3 className="font-display text-lg text-indigo-deep mb-3">{t("related_stays")}</h3>
              <div className="space-y-3">{data.related.slice(0, 4).map((e) => <EntityCard key={e.id} e={e} compact />)}</div>
            </div>
          )}
        </aside>
      </div>
    </article>
  );
}
